import type { GenEnum } from '@bufbuild/protobuf/codegenv2';
import { capitalCase } from 'change-case';

/**
 * A single resolved value of a protobuf enum.
 *
 * `name` is the *proto* name (`USER_ROLE_SYSOP`) — the canonical form shared by
 * the Go API, Postgres, Typesense and protobuf JSON. It is the only form
 * `fromJson` accepts, so it is what should be put on the wire and into state.
 *
 * `localName` is the generated TypeScript member name (`SYSOP`). It is retained
 * for display and for reading legacy records that were written in that form.
 */
export interface EnumOption<J extends string = string> {
  number: number;
  /** Proto name — the canonical wire/database representation. */
  name: J;
  /** Generated TypeScript enum member name. */
  localName: string;
  /** Humanised label suitable for dropdowns and tables. */
  label: string;
}

/** Any generated `*Schema` enum descriptor. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEnumSchema<E extends number, J extends string> = GenEnum<E, any> & {
  values: readonly { number: number; name: string; localName: string }[];
};

export interface EnumLabelOptions {
  /** Label used for the zero value. Defaults to `'Custom'`. */
  unspecifiedLabel?: string;
}

export interface EnumOptionsOptions extends EnumLabelOptions {
  /** Include the zero value in the returned options. Defaults to `false`. */
  includeUnspecified?: boolean;
}

const defaultUnspecifiedLabel = 'Custom';

function toLabel(
  option: { number: number; localName: string },
  { unspecifiedLabel = defaultUnspecifiedLabel }: EnumLabelOptions = {},
): string {
  return option.number === 0 ? unspecifiedLabel : capitalCase(option.localName);
}

/**
 * Resolves an enum value given its number, its proto name, or its generated
 * TypeScript name.
 *
 * Accepting all three is deliberate: records written before the protobuf-es v2
 * migration may hold any of them (numbers from `enumAsInteger` serialisation,
 * TypeScript names from the interim `canonicalEnum*` helpers, proto names from
 * the Go API). Lookups must tolerate every historical form, even though only
 * the proto name is written going forward.
 */
export function enumEntry<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  value: string | number | undefined | null,
  options?: EnumLabelOptions,
): EnumOption<J> | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const values = (schema as AnyEnumSchema<E, J>).values;
  const found =
    typeof value === 'number'
      ? values.find((option) => option.number === value)
      : values.find(
          (option) => option.name === value || option.localName === value,
        );

  if (!found) {
    return undefined;
  }

  return {
    number: found.number,
    name: found.name as J,
    localName: found.localName,
    label: toLabel(found, options),
  };
}

/**
 * All values of an enum, for dropdowns and other option lists.
 *
 * The zero value is excluded by default, matching the previous
 * `getEnumOptions` behaviour.
 */
export function enumOptions<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  { includeUnspecified = false, ...labelOptions }: EnumOptionsOptions = {},
): EnumOption<J>[] {
  return (schema as AnyEnumSchema<E, J>).values
    .filter((option) => includeUnspecified || option.number !== 0)
    .map((option) => ({
      number: option.number,
      name: option.name as J,
      localName: option.localName,
      label: toLabel(option, labelOptions),
    }));
}

/**
 * The proto name for a value — the form to send over the wire or store in state.
 *
 * Throws on an unresolvable value rather than returning a placeholder. The
 * previous helper returned `'[MISSING]'`, which `fromJson` then rejected far
 * from the original mistake; failing here points at the actual cause.
 */
export function enumName<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  value: string | number,
): J {
  const entry = enumEntry(schema, value);

  if (!entry) {
    throw new Error(
      `Unknown value ${JSON.stringify(value)} for enum ${schema.typeName}`,
    );
  }

  return entry.name;
}

/**
 * The numeric value for a value in any representation.
 *
 * Returns `0` (the zero value) when unresolvable. Callers such as the auth
 * role checks depend on this to deny by default.
 */
export function enumNumber<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  value: string | number | undefined | null,
): E {
  return (enumEntry(schema, value)?.number ?? 0) as E;
}

/** Humanised label for a value, for display in tables and dropdowns. */
export function enumLabel<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  value: string | number | undefined | null,
  options?: EnumLabelOptions,
): string {
  return enumEntry(schema, value, options)?.label ?? '[MISSING]';
}

/** Whether two values refer to the same enum value, in any representation. */
export function enumEquals<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  a: string | number | undefined | null,
  b: string | number | undefined | null,
): boolean {
  return enumNumber(schema, a) === enumNumber(schema, b);
}

/** Whether `needle` matches any of `haystack`, in any representation. */
export function enumIn<E extends number, J extends string>(
  schema: GenEnum<E, J>,
  needle: string | number | undefined | null,
  haystack: readonly (string | number | undefined | null)[],
): boolean {
  const value = enumNumber(schema, needle);
  return haystack.some((entry) => enumNumber(schema, entry) === value);
}

/**
 * Maps each enum value to its proto name, for use as Angular Material column
 * ids.
 *
 * Table column ids and the datasource `sort` field are both derived from this,
 * so a sort emitted by `matSort` is already in the canonical form the API
 * expects and needs no translation.
 */
export function enumColumnIds<E extends number, J extends string>(
  schema: GenEnum<E, J>,
): ReadonlyMap<E, J> {
  return new Map(
    (schema as AnyEnumSchema<E, J>).values.map((option) => [
      option.number as E,
      option.name as J,
    ]),
  );
}
